/**
 * Freezes the page behind a dialog, drawer or sheet. Use it instead of setting `document.body.style.overflow`:
 *
 *   useEffect(() => (open ? lockScroll() : undefined), [open]);
 *
 * - Locks both <html> and <body> (either one can be the page's scroller), and stops scroll chaining.
 * - Keeps the scrollbar's width as padding, so the page doesn't jump sideways when the scrollbar disappears.
 * - Counted: with a confirmation open on top of a dialog, closing the confirmation keeps the page locked
 *   until the dialog closes too. Unlocking twice is harmless.
 */
let count = 0;
let saved: { html: string; body: string; pad: string; overscroll: string } | null = null;

export function lockScroll(): () => void {
  if (typeof document === "undefined") return () => {};
  const html = document.documentElement;
  const body = document.body;
  if (count === 0) {
    const gap = window.innerWidth - html.clientWidth; // the scrollbar's width (0 on phones and overlay scrollbars)
    saved = { html: html.style.overflow, body: body.style.overflow, pad: body.style.paddingRight, overscroll: html.style.overscrollBehavior };
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    if (gap > 0) body.style.paddingRight = `${(parseFloat(getComputedStyle(body).paddingRight) || 0) + gap}px`;
  }
  count += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    count = Math.max(0, count - 1);
    if (count === 0 && saved) {
      html.style.overflow = saved.html;
      html.style.overscrollBehavior = saved.overscroll;
      body.style.overflow = saved.body;
      body.style.paddingRight = saved.pad;
      saved = null;
    }
  };
}
